
/* eslint-disable no-console */
import {
  advOutbrainShowHide,
  advRefresh,
  setDigitalData,
  initSocialShare,
  composeIntcmp,
  httpGetHTML,
  dynamicParselyTrackPageView,
  hydrateIntcmp,
  prepareImagesForNativeLazyLoading,
  anchorTabOnTop,
  getActiveTab,
  HERO_SLIM_HOOK,
  isSticky,
  tabScrollIntoView,
  ACTIVE_TAB_CLASS_PREFIX,
  updateInnerHTMLContent
} from '@sky-it-livedata/common-web-app-utils';
import { handleLGPHeadings } from './handle-lgp-headings';
import { intcmpMap } from './intcmp';

type ItemType = 'chip' | 'tabs';

type TabElement = {
  name?: string;
  $item?: HTMLElement | null;
  contentIdPrefix?: string;
  contentIdSuffix?: string;
  $content?: HTMLElement | null;
  isEmpty?: boolean;
  clickListener?: EventListener;
  innerTabsSelector?: string;
  $innerTabsWrapper?: HTMLElement | null;
  innerItems?: TabElement[];
};

type TabsSchema = {
  selector: string;
  $wrapper: HTMLElement | null;
  contentIdPrefix: string;
  items: TabElement[];
};

const tabsSchema: TabsSchema = {
  selector: '.ftbl__tabs-menu:not(.pp-menu-tabs):not(.ftbl__tabs-menu--chips)',
  contentIdPrefix: 'ftbl__tabs-switch__content',
  $wrapper: null,
  items: [
    {
      name: 'preview',
    },
    {
      name: 'live',
      innerTabsSelector: '.ftbl__tabs-menu--chips',
      contentIdPrefix: 'ftbl__chip-switch__content',
    },
    {
      name: 'risultati',
      innerTabsSelector: '.ftbl__tabs-menu--chips',
      contentIdPrefix: 'ftbl__motorsports-tabs-switch__content',
    },
  ],
};

// Yield to main pattern
const yieldToMain = (delay: number) => new Promise((resolve) => setTimeout(resolve, delay));

export async function handleLgpTabs() {
  await yieldToMain(0);
  const tabsMenu = document.querySelector<HTMLElement>(tabsSchema.selector);
  const isLGP = tabsMenu?.dataset.section === 'lgp';
  if (!tabsMenu || !isLGP) {
    return;
  }
  updateTabs();
  fetchEmptyTabs();
  advOutbrainShowHide(getActiveTab());
  handleLGPHeadings();
}

function triggerAnalytics(dataTracking = '', tabText: string) {
  setDigitalData(dataTracking, { intcmp: composeIntcmp(tabText, intcmpMap.intcmpLgpTemplate), quartoLivello: tabText });
  (window as any)._satellite?.track('cambio_tab', { tab_label: tabText.trim() });
  localStorage.setItem('intcmp', composeIntcmp(tabText, intcmpMap.intcmpLgpTemplate));
}

function fetchEmptyTabs() {
  tabsSchema.items.forEach((tab) => {
    const dataTabUrl = tab.$item?.getAttribute('data-tab-url');
    if (!dataTabUrl || !tab.isEmpty || !tab.$content) {
      return;
    }
    fetchHtml(dataTabUrl, (html) => {

      const tabContent = tab.$content;

      updateInnerHTMLContent({ tabContent: tabContent!, html });

      updateTabs();
      prepareImagesForNativeLazyLoading(tabContent!);
      hydrateIntcmp(tabContent!);
      handleLGPHeadings();

      tab.innerItems?.forEach((innerTab) => {
        const innerDataTabUrl = innerTab.$item?.getAttribute('data-tab-url');
        if (!innerDataTabUrl || !innerTab.isEmpty || !innerTab.$content) {
          return;
        }
        fetchHtml(innerDataTabUrl, (innerHtml) => {

          const innerTabContent = innerTab.$content;

          updateInnerHTMLContent({ tabContent: innerTabContent!, html: innerHtml });

          updateTabs();
          prepareImagesForNativeLazyLoading(innerTabContent!);
          hydrateIntcmp(innerTabContent!);
          handleLGPHeadings();
        });
      });

      console.info('[HANDLE-LGP-TABS:174] tab hydrated: ', { dataTabUrl, tabContent, html });
    });
  });
}

function scrollTabWithDelay(item: HTMLElement) {
  // When HERO is STICKY tabScrollIntoView() doesn't work without setTimeout()
  const scrollTimeout = isSticky(document.querySelector(HERO_SLIM_HOOK)) ? 100 : 0;
  setTimeout(() => tabScrollIntoView(item), scrollTimeout);
}

function activateTabByUrl(tabText: string) {
  const resultsChipsList: HTMLElement | null = document.querySelector('.ftbl__tabs-menu--chips:not(.j-tabs-menu)');
  const liveChipsList: HTMLElement | null = document.querySelector('.ftbl__tabs-menu--chips.j-tabs-menu');

  const activeList = tabText === 'risultati' ? resultsChipsList : liveChipsList;

  const { pathname } = window.location;
  const activeElement = activeList?.querySelector(`[data-url="${pathname}"]`);
  const activeId = extractId(activeElement?.id || '');

  if (activeId) {
    activeElement?.parentElement?.setAttribute('data-state', `active-tab--${activeId}`);
  }
}

function activateFirstChip(id: string) {
  const tabContent = document.getElementById(`ftbl__tabs-switch__content-${id}`);
  if (!tabContent) {
    return;
  }
  const fisrtChip = tabContent.querySelector('.ftbl__tabs-menu--chips li');
  if (!fisrtChip) {
    return;
  }
  const chipId = extractId(fisrtChip.id);
  fisrtChip.closest('ul')?.setAttribute('data-state', `active-tab--${chipId}`);
}

function extractId(fullId: string) {
  return fullId.split('--')?.[1] || '';
}

function extractName(element?: HTMLElement) {
  return element?.textContent?.trim().toLowerCase() || '';
}

function getTabConfig(tabs: TabsSchema | TabElement, name: string) {
  const items = 'items' in tabs ? tabs.items : tabs.innerItems;
  return items?.find((tab) => tab.name === name) || {};
}

function updateTabs() {
  const $tabsWrapper = document.querySelector<HTMLElement>(tabsSchema.selector);
  if (!$tabsWrapper) {
    return;
  }

  tabsSchema.$wrapper = $tabsWrapper;

  Array.from(tabsSchema.$wrapper.children).forEach((tabElement) => {
    const tabEl = tabElement as HTMLElement;
    const name = extractName(tabEl);
    const tabConfig = getTabConfig(tabsSchema, name);
    if (!tabConfig) {
      // This ensure that only configured tabs are managed
      return;
    }
    if (!tabConfig.$item) {
      tabConfig.$item = tabEl;
    }

    if (tabEl.id && !tabConfig.$content) {
      tabConfig.contentIdSuffix = extractId(tabEl.id);
      tabConfig.$content = document.getElementById(`${tabsSchema.contentIdPrefix}-${tabConfig.contentIdSuffix}`);
      tabConfig.isEmpty = tabConfig.$content?.innerText.trim().length === 0;
    }

    if (!tabConfig.clickListener && tabConfig.$content) {
      tabConfig.clickListener = handleItemClick({
        menu: $tabsWrapper,
        item: tabEl,
        itemType: 'tabs',
        tabContent: tabConfig.$content,
      });
    }

    updateChips(tabConfig);
  });
}

function handleItemClick({
  menu,
  item,
  itemType,
  tabContent,
}: {
  menu: HTMLElement;
  item: HTMLElement;
  itemType: ItemType;
  tabContent: HTMLElement | null;
}) {
  const tabAttributes = getTabAttributes(item);
  const id = extractId(item.id);

  const listener = (_e: Event) => {
    scrollTabWithDelay(item);
    if (tabContent?.innerText.trim().length && id) {
      switchTab({ ...tabAttributes, menu, id, itemType, item });
    } else if (tabContent && tabAttributes.dataTabUrl && id) {
      fetchHtml(tabAttributes.dataTabUrl, (html) => {

        updateInnerHTMLContent({ tabContent, html });

        prepareImagesForNativeLazyLoading(tabContent);
        switchTab({ ...tabAttributes, menu, id, itemType, item });
        hydrateIntcmp(tabContent);
        updateTabs();
      });
    }
    advOutbrainShowHide(item);
  };

  item.addEventListener('click', listener);

  return listener;
}

function switchTab({
  menu,
  id,
  dataUrl = '',
  dataTracking = '',
  tabText,
  itemType,
}: {
  menu: HTMLElement | null;
  id: string;
  dataUrl: string;
  dataTracking: string;
  tabText: string;
  itemType?: ItemType;
  item?: HTMLElement;
}) {
  menu && menu.setAttribute('data-state', `${ACTIVE_TAB_CLASS_PREFIX}${id}`);
  anchorTabOnTop();
  initSocialShare();
  itemType !== 'chip' && triggerAnalytics(dataTracking, tabText);
  if (itemType === 'tabs') {
    activateFirstChip(id);
  }
  handleLGPHeadings();
  history.replaceState({}, '', dataUrl);
  dynamicParselyTrackPageView({ url: `${location.origin}${dataUrl}`, urlRef: location.href });

  /**
   * As of today, this function causes an error on the page due to _4SDexRefresh,
   * so make sure to keep it at the end.
   */
  advRefresh();
}

function getTabAttributes(item: HTMLElement) {
  return {
    dataUrl: item.getAttribute('data-url') || '',
    dataTabUrl: item.getAttribute('data-tab-url'),
    dataTracking: item.getAttribute('data-tracking') || '',
    tabText: item.getElementsByTagName('span')[0]?.innerHTML.toLocaleLowerCase() ?? '',
  };
}

function fetchHtml(url: string, callback: (data: string) => void) {
  httpGetHTML(url)
    .then(({ data, status }) => {
      if (status === 200) {
        callback(data);
      } else {
        console.error('[HANDLE-LGP-TABS] error response status ==! 200 tab not enabled!');
      }
    })
    .catch((error) => console.error(error));
}

const updateChips = (tabConfig: TabElement): void => {
  if (tabConfig.innerTabsSelector) {
    tabConfig.$innerTabsWrapper = tabConfig.$content?.querySelector(tabConfig.innerTabsSelector);
    tabConfig.$innerTabsWrapper?.querySelectorAll('li').forEach((innerTabElement: HTMLElement) => {
      const innerTabName = extractName(innerTabElement);
      const innerTabConfig = getTabConfig(tabConfig, innerTabName);
      if (!innerTabConfig.name) {
        innerTabConfig.name = innerTabName;
      }
      if (!innerTabConfig.$item) {
        innerTabConfig.$item = innerTabElement;
      }
      if (innerTabElement.id && !innerTabConfig.$content) {
        innerTabConfig.contentIdSuffix = extractId(innerTabElement.id);
        innerTabConfig.$content =
          document.getElementById(`${tabConfig.contentIdPrefix}-${innerTabConfig.contentIdSuffix}`) ||
          document.getElementById(`${tabConfig.contentIdPrefix}--${innerTabConfig.contentIdSuffix}`);
        innerTabConfig.isEmpty = innerTabConfig.$content?.innerText.trim().length === 0;
      }

      if (tabConfig.name === 'live') {
        tabConfig.$innerTabsWrapper && tabConfig.$innerTabsWrapper?.classList.add('live');
      } else if (tabConfig.name === 'risultati') {
        tabConfig.$innerTabsWrapper && tabConfig.$innerTabsWrapper?.classList.add('risultati');
        activateTabByUrl(tabConfig.name);
      }

      if (!innerTabConfig.clickListener && innerTabConfig.$content) {
        innerTabConfig.clickListener = handleItemClick({
          menu: tabConfig.$innerTabsWrapper!,
          item: innerTabElement,
          itemType: 'chip',
          tabContent: innerTabConfig.$content,
        });
      }

      if (!tabConfig.innerItems?.find((tab) => tab.name === innerTabConfig.name)) {
        tabConfig.innerItems = tabConfig.innerItems ? [...tabConfig.innerItems, innerTabConfig] : [innerTabConfig];
      }
    });
  }
};